import clsx from "clsx";
import { useId, useState } from "react";

interface AnimatedThemeToggleButtonProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  className?: string;
  disabled?: boolean;
  label?: string;
}

interface AnimatedThemeToggleIconProps {
  checked: boolean;
  hovered?: boolean;
  className?: string;
}

export function AnimatedThemeToggleIcon({
  checked,
  hovered = false,
  className,
}: AnimatedThemeToggleIconProps): React.JSX.Element {
  const clipPathId = useId();
  const displayChecked = hovered ? !checked : checked;

  return (
    <span className={className}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        fill="currentColor"
        strokeLinecap="round"
        viewBox="0 0 32 32"
        width="20"
        height="20"
        className="block size-full max-h-5 max-w-5"
      >
        <defs>
          <clipPath id={clipPathId}>
            <path
              d="M0-5h30a1 1 0 0 0 9 13v24H0Z"
              style={{
                transform: displayChecked ? "translate(-12px, 10px)" : "translate(0, 0)",
                transformBox: "fill-box",
                transformOrigin: "center",
                transition: "transform 0.35s ease-in-out",
              }}
            />
          </clipPath>
        </defs>

        <g clipPath={`url(#${clipPathId})`}>
          <circle
            cx="16"
            cy="16"
            r={displayChecked ? 10 : 7}
            style={{
              transition: "r 0.35s ease-in-out",
            }}
          />
          <g
            stroke="currentColor"
            strokeWidth="2.5"
            fill="none"
            style={{
              opacity: displayChecked ? 0 : 1,
              transform: displayChecked ? "rotate(-100deg) scale(0.5)" : "rotate(0deg) scale(1)",
              transformBox: "fill-box",
              transformOrigin: "center",
              transition: "transform 0.35s ease-in-out, opacity 0.35s ease-in-out",
            }}
          >
            <path d="M16 5.5v-4" />
            <path d="M16 30.5v-4" />
            <path d="M1.5 16h4" />
            <path d="M26.5 16h4" />
            <path d="m23.4 8.6 2.8-2.8" />
            <path d="m5.7 26.3 2.9-2.9" />
            <path d="m5.8 5.8 2.8 2.8" />
            <path d="m23.4 23.4 2.9 2.9" />
          </g>
        </g>
      </svg>
    </span>
  );
}

export function AnimatedThemeToggleButton({
  checked,
  defaultChecked = false,
  onCheckedChange,
  className,
  disabled = false,
  label = "Toggle theme",
}: AnimatedThemeToggleButtonProps): React.JSX.Element {
  const [internalChecked, setInternalChecked] = useState(defaultChecked);
  const isControlled = checked !== undefined;
  const isDark = isControlled ? checked : internalChecked;

  function handleClick(): void {
    if (disabled) {
      return;
    }

    const next = !isDark;

    if (!isControlled) {
      setInternalChecked(next);
    }

    onCheckedChange?.(next);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      aria-pressed={isDark}
      className={clsx(
        "inline-flex items-center justify-center rounded-full transition-all duration-300 active:scale-95",
        isDark ? "bg-black text-white" : "bg-white text-black",
        disabled && "cursor-not-allowed opacity-60",
        className,
      )}
    >
      <AnimatedThemeToggleIcon checked={isDark} />
    </button>
  );
}
